import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import {
  BUY, SELL, SectionHeader, SectionWrapper,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/enosikit/components/Chart/components/chartConstants';
import { getDirectionHeaderLabel } from 'src/enosikit/components/Chart/helpers/cards';
import {
  buildTradeTooltipGroups, extractTradeValues,
  getTooltipFormatFunc, getTooltipItemValue,
  isOtherTooltipItemKey,
} from 'src/enosikit/components/Chart/helpers/tooltip';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
  TRADE_TYPE_ORDER,
} from 'src/util/constants';
import { getTradeTypeLabel } from 'src/util/i18n/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import getMeterLabel from 'src/util/meter';

import { getEnergyOriginLabel } from '../helpers/propertyShowChart';

/**
 * Resolves the display label for a Property trade tooltip line item.
 * @param {import('react-intl').IntlShape} intl i18n intl object.
 * @param {object} datum Tooltip datum for one series.
 * @param {object} chartView Property chart aggregation and grouping options.
 * @returns {string|null} Line item label.
 */
export const formatItemLabel = (intl, datum, chartView) => {
  if (isOtherTooltipItemKey(datum.key)) {
    return datum.label;
  }
  if (chartView.aggregateBy === DATA_AGGREGATE_BY_METER) {
    return getMeterLabel(datum.meter?.identifier, datum.meter?.title)
      || getEnergyOriginLabel(intl, datum.label, chartView);
  }
  if (chartView.groupBy === DATA_GROUP_BY_TRADE_TYPE && datum.property?.title) {
    return datum.property.title;
  }
  return getEnergyOriginLabel(intl, datum.label, chartView);
};

/**
 * Returns the nested sub-rows to render for a trade-type group. A group with a
 * single item collapses onto its trade-type total header whenever that item's
 * resolved display label is byte-for-byte identical to the header, so a header
 * and a sub-row never render the same text. Comparing the resolved label — the
 * exact string `formatItemLabel` will render — keeps this correct across both
 * aggregation modes: it drops a real series carrying no distinguishing identity
 * (community, residual and untraded energy, or a meter-aggregated series with no
 * meter) and a typed "Other" residual in its plain off-page-only form, while
 * retaining a distinguishing "Other {type}" residual — produced when its paired
 * on-page series is zero at the hovered interval. Multi-item groups always render
 * their full breakdown.
 * @param {import('react-intl').IntlShape} intl i18n intl object.
 * @param {object} group Trade-type group from `buildTradeTooltipGroups`.
 * @param {object} chartView Property chart aggregation and grouping options.
 * @param {string} groupLabel Resolved trade-type header label for the group.
 * @returns {Array<object>} Nested items to render.
 */
export const getGroupNestedItems = (intl, group, chartView, groupLabel) => {
  if (group.items.length !== 1) {
    return group.items;
  }
  const [item] = group.items;
  return formatItemLabel(intl, item, chartView) === groupLabel ? [] : group.items;
};

const formatItemSubLabel = (intl, datum, chartView) => {
  if (isOtherTooltipItemKey(datum.key)) {
    return null;
  }
  if (chartView.aggregateBy !== DATA_AGGREGATE_BY_METER) {
    return datum.subLabel;
  }
  if (chartView.groupBy !== DATA_GROUP_BY_COUNTERPARTY) {
    return null;
  }

  const counterPartyLabel = getEnergyOriginLabel(intl, datum.label, chartView);
  if (!counterPartyLabel) {
    return datum.subLabel;
  }
  return datum.subLabel
    ? `${counterPartyLabel} (${datum.subLabel})`
    : counterPartyLabel;
};

/**
 * Renders a single tooltip line, optionally indented as a nested sub-item.
 * @param {object} props Component props.
 * @param {React.ReactNode} props.label Line label.
 * @param {string} props.subLabel Secondary line label.
 * @param {React.ReactNode} props.value Formatted line value.
 * @param {boolean} props.nested Whether the line is a nested sub-item.
 * @returns {React.ReactElement} Tooltip line.
 */
function TradeTooltipRow({
  label = null, subLabel = null, value = null, nested = false,
}) {
  return (
    <Row className="mb-1">
      <Col className={nested ? 'pe-0 ps-4' : 'pe-0'}>
        <span>{label}</span>
        {subLabel && (
          <>
            <br />
            <small>{`(${subLabel})`}</small>
          </>
        )}
      </Col>
      <Col xs="auto">{value}</Col>
    </Row>
  );
}

TradeTooltipRow.propTypes = {
  label: PropTypes.node,
  nested: PropTypes.bool,
  subLabel: PropTypes.string,
  value: PropTypes.node,
};

/**
 * Renders the trade-type groups of one tooltip section. Each group renders a
 * trade-type total, followed by indented sub-rows unless grouping by trade type,
 * in which case the total already conveys the value and the sub-rows are omitted.
 * A lone sub-row that would only duplicate its trade-type total header is also
 * omitted (see `getGroupNestedItems`).
 * @param {object} props Component props.
 * @param {Array<object>} props.items Tooltip items belonging to the section.
 * @param {object} props.chartView Property chart aggregation and grouping options.
 * @param {Function} props.tooltipFormatFunc Value formatter.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement} Section body.
 */
function TradeTooltipGroups({
  items, chartView, tooltipFormatFunc, unitLabel = null,
}) {
  const intl = useIntl();
  // When grouping by trade type, the group total already conveys the trade-type
  // value, so the nested sub-rows would only repeat it. Suppress them to keep
  // the tooltip to a single line per trade type.
  const showNestedItems = chartView.groupBy !== DATA_GROUP_BY_TRADE_TYPE;
  // `items` is referentially stable across mousemove-only re-renders while the
  // pointer stays over the same bar (see `getTooltipDataFunc` in ChartTrade),
  // so this skips the O(N log N) grouping/sorting work on those re-renders.
  const groups = useMemo(() => buildTradeTooltipGroups(
    items,
    {
      excludeZero: true,
      tradeTypeOrder: TRADE_TYPE_ORDER,
    },
  ), [items]);

  return (
    <>
      {groups.map((group) => {
        const groupLabel = getTradeTypeLabel(intl, group.tradeType);
        const nestedItems = showNestedItems
          ? getGroupNestedItems(intl, group, chartView, groupLabel)
          : [];

        return (
          <React.Fragment key={group.tradeType}>
            <TradeTooltipRow
              label={groupLabel}
              value={tooltipFormatFunc(group.total, unitLabel)}
            />
            {nestedItems.map((datum) => (
              <TradeTooltipRow
                key={datum.key}
                nested
                label={formatItemLabel(intl, datum, chartView)}
                subLabel={formatItemSubLabel(intl, datum, chartView)}
                value={tooltipFormatFunc(datum.value, unitLabel)}
              />
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

TradeTooltipGroups.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tooltipFormatFunc: PropTypes.func.isRequired,
  unitLabel: PropTypes.string,
};

/**
 * Renders the Property trade chart tooltip, preserving meter and counterparty labels.
 * @param {object} props Component props.
 * @param {object} props.chartView Property chart aggregation and grouping options.
 * @param {object} props.data Buy and sell trade tooltip data.
 * @param {string} props.unit Chart unit.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement|null} Property trade tooltip content.
 */
export default function TradeChartTooltip({
  chartView = null, data = null, unit = null, unitLabel = null,
}) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  // `data` is referentially stable across mousemove-only re-renders while the
  // pointer stays over the same bar (see `getTooltipDataFunc` in ChartTrade),
  // so memoizing per-direction items on it keeps their references stable too,
  // letting `TradeTooltipGroups`'s `useMemo` actually skip recomputing groups
  // on those re-renders. Computed unconditionally (before the `!data` early
  // return below) so the hook call order stays stable across renders.
  const itemsByDirection = useMemo(() => Object.fromEntries(
    [BUY, SELL].map((direction) => [
      direction,
      Object.values(data?.[direction] || {})
        .filter(Boolean)
        .filter((item) => getTooltipItemValue(item.value) !== 0),
    ]),
  ), [data]);

  if (!data || !chartView) {
    return null;
  }

  const values = extractTradeValues(data);
  const tooltipFormatFunc = getTooltipFormatFunc(unit, values, intlCurrency);

  return (
    <>
      {[BUY, SELL].map((direction) => {
        const items = itemsByDirection[direction];
        if (items.length === 0) {
          return null;
        }

        const directionHeader = getDirectionHeaderLabel(intl, direction);

        return (
          <SectionWrapper key={direction}>
            <SectionHeader>{directionHeader}</SectionHeader>
            <TradeTooltipGroups
              chartView={chartView}
              items={items}
              tooltipFormatFunc={tooltipFormatFunc}
              unitLabel={unitLabel}
            />
          </SectionWrapper>
        );
      })}
    </>
  );
}

TradeChartTooltip.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PROPERTY,
    ]),
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]),
  }),
  data: PropTypes.shape({
    buy: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      meter: PropTypes.shape({
        identifier: PropTypes.string,
        title: PropTypes.string,
      }),
      property: PropTypes.shape({}),
      subLabel: PropTypes.string,
      user: PropTypes.shape({}),
      value: PropTypes.number,
    })),
    sell: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      meter: PropTypes.shape({
        identifier: PropTypes.string,
        title: PropTypes.string,
      }),
      property: PropTypes.shape({}),
      subLabel: PropTypes.string,
      user: PropTypes.shape({}),
      value: PropTypes.number,
    })),
  }),
  unit: PropTypes.oneOf([UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY]),
  unitLabel: PropTypes.string,
};
